import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Archive, Copy, X } from 'lucide-react';

export interface OSArchiveOptions {
    createNewVersion: boolean;
}

interface IngredientArchiveModalProps {
    show: boolean;
    onCancel: () => void;
    onConfirm: (reason: string, validTo?: string, options?: OSArchiveOptions) => Promise<void>;
    activeVersion?: string;
    validFrom?: string;
    validTo?: string;
    title?: string;
    reasonLabel?: string;
    confirmButtonText?: string;
    iconColor?: 'amber' | 'blue';
    createNewVersionOnly?: boolean;
}

const OSArchiveModal: React.FC<IngredientArchiveModalProps> = ({
    show,
    onCancel,
    onConfirm,
    title,
    reasonLabel,
    confirmButtonText,
    iconColor = 'amber',
    createNewVersionOnly = false,
    activeVersion,
    validFrom,
    validTo,
}) => {
    const { t } = useTranslation();
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);
    const [createNewVersion, setCreateNewVersion] = useState(createNewVersionOnly);

    useEffect(() => {
        if (show) setCreateNewVersion(createNewVersionOnly);
    }, [show, createNewVersionOnly]);

    if (!show) return null;

    const isBlue = iconColor === 'blue';
    const isValid = reason.trim().length >= 10;
    const resolvedTitle = title ?? t('ingredients.archive.title', 'Архивирај');
    const resolvedReasonLabel = reasonLabel ?? 'Евиденција на измена';
    const resolvedConfirmText = confirmButtonText ?? t('ingredients.archive.confirm', 'Архивирај');

    const reset = () => {
        setReason('');
        setCreateNewVersion(createNewVersionOnly);
    };

    const handleConfirm = async () => {
        if (!isValid) return;
        setSaving(true);
        try {
            await onConfirm(reason.trim(), validTo, { createNewVersion });
            reset();
            onCancel();
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        reset();
        onCancel();
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 backdrop-blur-sm">
            <div className="bg-white p-8 rounded-2xl shadow-2xl w-[760px] max-w-[calc(100vw-32px)] border border-gray-200">
                <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${isBlue ? 'bg-blue-100' : 'bg-amber-100'}`}>
                            {isBlue
                                ? <Copy className="w-5 h-5 text-blue-600" />
                                : <Archive className="w-5 h-5 text-amber-600" />
                            }
                        </div>
                        <h2 className="text-lg font-semibold text-gray-900">{resolvedTitle}</h2>
                    </div>
                    <button
                        onClick={handleCancel}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="grid grid-cols-[220px_1fr_210px] gap-5 mb-5">
                    <div className="border border-gray-200 rounded-lg overflow-hidden self-start">
                        <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600 uppercase">
                            Активна верзија
                        </div>
                        <div className="divide-y divide-gray-200">
                            <div className="grid grid-cols-[86px_1fr]">
                                <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50 border-r border-gray-200">Верзија</div>
                                <div className="px-3 py-2 text-sm font-medium text-gray-900">{activeVersion || '-'}</div>
                            </div>
                            <div className="grid grid-cols-[86px_1fr]">
                                <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50 border-r border-gray-200">Датум од</div>
                                <div className="px-3 py-2 text-sm text-gray-900">{validFrom || '-'}</div>
                            </div>
                            <div className="grid grid-cols-[86px_1fr]">
                                <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50 border-r border-gray-200">Датум до</div>
                                <div className="px-3 py-2 text-sm text-gray-900">{validTo || '-'}</div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            {resolvedReasonLabel}
                            <span className="text-red-500 ml-1">*</span>
                        </label>
                        <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={5}
                            placeholder="Внеси евиденција на измена (мин. 5 карактери)"
                            className={`w-full border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 ${
                                reason.length > 0 && !isValid
                                    ? 'border-red-300 focus:ring-red-200'
                                    : isBlue
                                        ? 'border-gray-300 focus:ring-blue-200'
                                        : 'border-gray-300 focus:ring-amber-200'
                            }`}
                            autoFocus
                        />
                        {reason.length > 0 && !isValid && (
                            <p className="text-xs text-red-500 mt-1">
                                {t('ingredients.archive.reasonMin', 'Минимум 10 карактери')}
                            </p>
                        )}
                    </div>

                    {!createNewVersionOnly && <div className="border border-gray-200 rounded-lg p-3 self-start">
                        <div className="text-xs font-semibold text-gray-600 uppercase mb-3">Опции</div>
                        <label className="flex items-start gap-2 text-sm text-gray-800 mb-3">
                            <input
                                type="radio"
                                name="osArchiveMode"
                                checked={!createNewVersion}
                                onChange={() => setCreateNewVersion(false)}
                                className="mt-1 h-4 w-4 border-gray-300 text-amber-600 focus:ring-amber-500"
                            />
                            <span>
                                Архивирај
                                <span className="block text-xs text-gray-500 font-normal">Само архивира и го отстранува од Норматив.</span>
                            </span>
                        </label>
                        <label className="flex items-start gap-2 text-sm text-gray-800">
                            <input
                                type="radio"
                                name="osArchiveMode"
                                checked={createNewVersion}
                                onChange={() => setCreateNewVersion(true)}
                                className="mt-1 h-4 w-4 border-gray-300 text-amber-600 focus:ring-amber-500"
                            />
                            <span>
                                Архивирај и отвори нова верзија
                                <span className="block text-xs text-gray-500 font-normal">Прави нова верзија (целосна копија) и останува во Норматив.</span>
                            </span>
                        </label>
                    </div>}
                </div>

                <div className="flex justify-end gap-2">
                    <button
                        onClick={handleCancel}
                        disabled={saving}
                        className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                        {t('ingredients.archive.cancel', 'Откажи')}
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={!isValid || saving}
                        className={`px-4 py-2 text-sm rounded-lg text-white transition-colors disabled:opacity-50 flex items-center gap-1.5 ${
                            isBlue
                                ? 'bg-blue-600 hover:bg-blue-700'
                                : 'bg-amber-500 hover:bg-amber-600'
                        }`}
                    >
                        {isBlue ? <Copy size={14} /> : <Archive size={14} />}
                        {resolvedConfirmText}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OSArchiveModal;
